import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Refresh,
  Assessment,
  Sync,
  Timeline,
  CheckCircle,
  Error,
  Warning,
  Info,
  Clear,
  CalendarToday,
  TrendingUp,
  BarChart
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import FullScreenLayout from './layouts/FullScreenLayout';
import styled from '@emotion/styled';
import { createSalesSyncSSE, getPosSystems, getSalesStats } from '../services/api';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 3),
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  fontWeight: 700,
  fontSize: '2.5rem',
  textAlign: 'center',
  padding: theme.spacing(2, 0),
  borderBottom: `2px solid ${theme.palette.divider}`,
}));

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '100%',
  margin: '0 auto',
  backgroundColor: theme.palette.background.default,
}));

const LogContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  border: '1px solid #e9ecef',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  maxHeight: '300px',
  overflowY: 'auto',
  fontFamily: 'monospace',
  fontSize: '12px',
  '& .log-entry': {
    marginBottom: '4px',
    padding: '2px 0',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
  },
  '& .log-entry.error': {
    color: theme.palette.error.main,
  },
  '& .log-entry.success': {
    color: theme.palette.success.main,
  },
  '& .log-entry.info': {
    color: theme.palette.info.main,
  },
  '& .log-entry.warning': {
    color: theme.palette.warning.main,
  },
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
  color: 'white',
  textAlign: 'center',
  height: '120px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${theme.palette.divider}`,
  '& .metric-row': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  '& .metric-label': {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  '& .metric-value': {
    fontWeight: 600,
    '&.new': { color: theme.palette.success.main },
    '&.updated': { color: theme.palette.info.main },
    '&.warning': { color: theme.palette.warning.main },
    '&.error': { color: theme.palette.error.main },
    '&.success': { color: theme.palette.success.main },
    '&.skipped': { color: theme.palette.grey[600] },
    '&.rate': { color: theme.palette.primary.main },
    '&.date': { fontSize: '0.9rem', color: theme.palette.text.primary },
  },
  '& .ratio-values': {
    display: 'flex',
    gap: theme.spacing(2),
    '& .new-ratio': { color: theme.palette.success.main, fontWeight: 600 },
    '& .updated-ratio': { color: theme.palette.info.main, fontWeight: 600 },
  },
}));

const MonthlyProgressCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${theme.palette.divider}`,
  '&.processing': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
  },
  '&.completed': {
    borderColor: theme.palette.success.main,
    backgroundColor: theme.palette.success.light + '10',
  },
  '&.error': {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.light + '10',
  },
}));

const MonthlyStatsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const SalesSync = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedPosId, setSelectedPosId] = useState('');
  const [historical, setHistorical] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [unmatchedItems, setUnmatchedItems] = useState([]);
  const [detailedSummary, setDetailedSummary] = useState(null);
  const [posOptions, setPosOptions] = useState([]);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [syncStartTime, setSyncStartTime] = useState(null);
  const [showFullSalesTable, setShowFullSalesTable] = useState(false);
  const [showFullUnmatchedTable, setShowFullUnmatchedTable] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [maxReconnectAttempts] = useState(5);
  const [monthlyProgress, setMonthlyProgress] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [totalMonths, setTotalMonths] = useState(0);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  
  const eventSourceRef = useRef(null);
  const logContainerRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const lastSyncOptionsRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize date inputs with reasonable defaults
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(lastMonth.toISOString().split('T')[0]);

    // Load POS systems
    loadPosOptions();

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const loadPosOptions = async () => {
    try {
      const response = await getPosSystems();
      setPosOptions(response.data || []);
    } catch (error) {
      console.error('Error loading POS systems:', error);
      addLogEntry('Failed to load POS systems', 'error');
    }
  };

  const addLogEntry = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const entry = {
      id: Date.now(),
      timestamp,
      message,
      type
    };
    
    setLogs(prev => [...prev, entry]);
    
    // Auto-scroll to bottom
    setTimeout(() => {
      if (logContainerRef.current) {
        logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const updateStatus = (message, type) => {
    setStatus({ message, type });
  };

  const updateProgress = (current, total, text) => {
    if (total > 0) {
      const percentage = Math.round((current / total) * 100);
      setProgress(percentage);
    }
    if (text) {
      setProgressText(text);
    }
  };

  const handleSyncMessage = (data) => {
    switch (data.type) {
      case 'connected':
        addLogEntry('Connected to sales sync stream', 'success');
        updateStatus('Connected - Starting sync...', 'info');
        break;

      case 'ping':
        // Ignore ping messages - they're just keep-alive
        break;

      case 'pos_start':
        addLogEntry(`Starting POS system: ${data.posName}`, 'info');
        if (data.totalMonths) {
          setTotalMonths(data.totalMonths);
          addLogEntry(`Will process ${data.totalMonths} months for ${data.posName}`, 'info');
        }
        updateStatus(`Processing POS: ${data.posName}...`, 'info');
        break;

      case 'month_start':
        addLogEntry(`Starting month ${data.month} for ${data.posName} (${data.monthIndex}/${data.totalMonths})`, 'info');
        setCurrentMonth(data.month);
        setCurrentMonthIndex(data.monthIndex);
        
        // Update monthly progress
        setMonthlyProgress(prev => {
          const newProgress = [...prev];
          const existingIndex = newProgress.findIndex(p => p.month === data.month && p.posName === data.posName);
          
          if (existingIndex >= 0) {
            newProgress[existingIndex] = {
              ...newProgress[existingIndex],
              status: 'processing',
              fetchProgress: 0,
              processProgress: 0
            };
          } else {
            newProgress.push({
              month: data.month,
              posName: data.posName,
              status: 'processing',
              fetchProgress: 0,
              processProgress: 0,
              monthIndex: data.monthIndex,
              totalMonths: data.totalMonths
            });
          }
          
          return newProgress.sort((a, b) => a.monthIndex - b.monthIndex);
        });
        
        updateProgress(
          ((data.monthIndex - 1) / data.totalMonths) * 100, 
          100, 
          `Processing ${data.month} for ${data.posName}...`
        );
        break;

      case 'month_fetch_progress':
        addLogEntry(`Fetched ${data.fetched} sales for ${data.month} (${data.posName})`, 'info');
        
        setMonthlyProgress(prev => {
          const newProgress = [...prev];
          const existingIndex = newProgress.findIndex(p => p.month === data.month && p.posName === data.posName);
          
          if (existingIndex >= 0) {
            newProgress[existingIndex] = {
              ...newProgress[existingIndex],
              fetchProgress: data.fetched,
              fetchedTotal: data.fetched
            };
          }
          
          return newProgress;
        });
        break;

      case 'month_process_progress':
        addLogEntry(`Processed ${data.processed}/${data.total} sales for ${data.month} (${data.posName})`, 'info');
        
        setMonthlyProgress(prev => {
          const newProgress = [...prev];
          const existingIndex = newProgress.findIndex(p => p.month === data.month && p.posName === data.posName);
          
          if (existingIndex >= 0) {
            newProgress[existingIndex] = {
              ...newProgress[existingIndex],
              processProgress: data.processed,
              processTotal: data.total,
              monthStats: data.monthStats
            };
          }
          
          return newProgress;
        });
        
        const overallProgress = ((data.monthIndex - 1) / data.totalMonths) * 100 + 
                               (data.processed / data.total) * (100 / data.totalMonths);
        updateProgress(overallProgress, 100, `Processing ${data.month}: ${data.processed}/${data.total} sales`);
        break;

      case 'month_complete':
        addLogEntry(`Completed month ${data.month} for ${data.posName}`, 'success');
        
        setMonthlyProgress(prev => {
          const newProgress = [...prev];
          const existingIndex = newProgress.findIndex(p => p.month === data.month && p.posName === data.posName);
          
          if (existingIndex >= 0) {
            newProgress[existingIndex] = {
              ...newProgress[existingIndex],
              status: 'completed',
              monthStats: data.monthStats,
              processProgress: data.monthStats.salesProcessed,
              processTotal: data.monthStats.salesProcessed
            };
          }
          
          return newProgress;
        });

        // Add to monthly stats for aggregation
        setMonthlyStats(prev => {
          const existing = prev.find(s => s.month === data.month);
          if (existing) {
            // Update existing month stats by aggregating POS data
            return prev.map(s => s.month === data.month ? {
              ...s,
              totalSales: s.totalSales + data.monthStats.salesProcessed,
              totalNewSales: s.totalNewSales + data.monthStats.newSales,
              totalUpdatedSales: s.totalUpdatedSales + data.monthStats.updatedSales,
              totalItems: s.totalItems + data.monthStats.itemsProcessed,
              totalUnmatchedItems: s.totalUnmatchedItems + data.monthStats.unmatchedItems,
              posSystemsProcessed: s.posSystemsProcessed + 1
            } : s);
          } else {
            // Add new month stats
            return [...prev, {
              month: data.month,
              year: data.monthStats.year,
              monthNumber: data.monthStats.monthNumber,
              totalSales: data.monthStats.salesProcessed,
              totalNewSales: data.monthStats.newSales,
              totalUpdatedSales: data.monthStats.updatedSales,
              totalItems: data.monthStats.itemsProcessed,
              totalUnmatchedItems: data.monthStats.unmatchedItems,
              totalSkippedNonInventoryItems: data.monthStats.skippedNonInventoryItems,
              posSystemsProcessed: 1,
              errors: data.monthStats.errors
            }];
          }
        });
        
        updateProgress(
          (data.monthIndex / data.totalMonths) * 100, 
          100, 
          `Completed ${data.month} - ${data.monthStats.salesProcessed} sales processed`
        );
        break;

      case 'pos_complete':
        addLogEntry(`Completed POS: ${data.posName} (${data.monthsProcessed} months)`, 'success');
        updateStatus(`Completed processing: ${data.posName}`, 'success');
        break;

      case 'progress':
        // Handle legacy progress messages
        if (data.type === 'fetch_progress') {
          addLogEntry(`Fetched ${data.totalFetched} sales from ${data.posName}`, 'info');
          updateProgress(data.totalFetched, data.totalFetched + 1000, `Fetching from ${data.posName}...`);
        } else if (data.type === 'process_progress') {
          addLogEntry(`Processed ${data.processed}/${data.total} sales from ${data.posName}`, 'info');
          updateProgress(data.processed, data.total, `Processing ${data.posName}...`);
        } else if (data.type === 'populate_start') {
          addLogEntry('Starting populateItemsByLocation...', 'info');
          updateStatus('Finalizing inventory updates...', 'info');
        }
        break;

      case 'completed':
        addLogEntry('Sales sync completed successfully!', 'success');
        updateStatus('Sync completed successfully!', 'success');
        setStats(data.stats);
        updateProgress(100, 100, 'Completed!');
        setIsRunning(false);
        setCurrentMonth(null);
        break;

      case 'sales_table':
        addLogEntry(`Received sales data (${data.data.length} records)`, 'success');
        setSalesData(data.data);
        break;

      case 'unmatched_items':
        addLogEntry(`Received unmatched items data (${data.data.length} items)`, 'success');
        setUnmatchedItems(data.data);
        break;

      case 'detailed_summary':
        addLogEntry('Received detailed sync summary with comprehensive analytics', 'success');
        setDetailedSummary(data.data);
        break;

      case 'error':
        addLogEntry(`Error: ${data.message}`, 'error');
        updateStatus(`Error: ${data.message}`, 'error');
        setIsRunning(false);
        
        // Mark current month as error if processing
        if (currentMonth) {
          setMonthlyProgress(prev => 
            prev.map(p => p.month === currentMonth && p.status === 'processing' 
              ? { ...p, status: 'error' } 
              : p
            )
          );
        }
        break;

      case 'close':
        addLogEntry('Connection closing...', 'info');
        setIsRunning(false);
        break;

      default:
        addLogEntry(`${data.message || JSON.stringify(data)}`, 'info');
    }
  };

  const startSalesSync = (isReconnect = false) => {
    if (isRunning && !isReconnect) return;

    // Reset monthly progress and stats when starting new sync
    if (!isReconnect) {
      setMonthlyProgress([]);
      setMonthlyStats([]);
      setCurrentMonth(null);
      setTotalMonths(0);
      setCurrentMonthIndex(0);
    }

    const options = {
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      posId: selectedPosId || undefined,
      historical
    };

    // Store options for potential reconnection
    lastSyncOptionsRef.current = options;

    if (!isReconnect) {
      setSyncStartTime(Date.now());
      setIsRunning(true);
      setProgress(0);
      setProgressText('');
      setStats(null);
      setSalesData([]);
      setUnmatchedItems([]);
      setDetailedSummary(null);
      setConnectionAttempts(0);
    }

    const logMessage = isReconnect ? 
      `Reconnecting to sales sync stream (attempt ${connectionAttempts + 1}/${maxReconnectAttempts})...` : 
      'Connecting to sales sync stream...';
    
    addLogEntry(logMessage, 'info');
    updateStatus(isReconnect ? 'Reconnecting...' : 'Connecting to sync stream...', 'info');

    try {
      // Clean up previous connection if exists
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      eventSourceRef.current = createSalesSyncSSE(options);

      eventSourceRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Reset connection attempts on successful message
          if (connectionAttempts > 0) {
            setConnectionAttempts(0);
            addLogEntry('Connection restored successfully', 'success');
          }
          
          handleSyncMessage(data);
        } catch (error) {
          addLogEntry(`Error parsing message: ${error.message}`, 'error');
        }
      };

      eventSourceRef.current.onopen = () => {
        if (isReconnect) {
          addLogEntry('Reconnected successfully', 'success');
          updateStatus('Reconnected - sync continuing...', 'info');
        }
      };

      eventSourceRef.current.onerror = (event) => {
        const newAttempts = connectionAttempts + 1;
        
        if (newAttempts <= maxReconnectAttempts && isRunning) {
          addLogEntry(`Connection lost. Attempting reconnection in 5 seconds (${newAttempts}/${maxReconnectAttempts})`, 'warning');
          updateStatus(`Connection lost - reconnecting...`, 'warning');
          
          setConnectionAttempts(newAttempts);
          
          // Attempt reconnection after 5 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            startSalesSync(true);
          }, 5000);
        } else {
          addLogEntry('Connection error occurred - max reconnection attempts reached', 'error');
          updateStatus('Connection failed - manual restart required', 'error');
          stopSalesSync();
        }
      };

    } catch (error) {
      addLogEntry(`Failed to start sync: ${error.message}`, 'error');
      updateStatus('Failed to start sync', 'error');
      setIsRunning(false);
    }
  };

  const stopSalesSync = () => {
    // Clear any pending reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setIsRunning(false);
    setConnectionAttempts(0);

    if (syncStartTime) {
      const duration = Math.round((Date.now() - syncStartTime) / 1000);
      addLogEntry(`Sync stopped after ${duration} seconds`, 'info');
    }
  };

  const getStatusIcon = () => {
    switch (status.type) {
      case 'success': return <CheckCircle color="success" />;
      case 'error': return <Error color="error" />;
      case 'warning': return <Warning color="warning" />;
      default: return <Info color="info" />;
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '0s';
    const seconds = Math.round(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
  };

  return (
    <FullScreenLayout
      header={
        <Title variant="h4" gutterBottom>
          <Sync sx={{ mr: 2, verticalAlign: 'middle' }} />
          Sales Synchronization
        </Title>
      }
      mainPanel={
        <PageContainer>
          {/* Controls */}
          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              Sync Configuration
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={2.5}>
                <StyledTextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled={isRunning}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <StyledTextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled={isRunning}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <StyledFormControl fullWidth>
                  <InputLabel>POS System</InputLabel>
                  <Select
                    value={selectedPosId}
                    onChange={(e) => setSelectedPosId(e.target.value)}
                    disabled={isRunning}
                  >
                    <MenuItem value="">All POS Systems</MenuItem>
                    {posOptions.map((pos) => (
                      <MenuItem key={pos.pos_id} value={pos.pos_id}>
                        {pos.name}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={historical}
                      onChange={(e) => setHistorical(e.target.checked)}
                      disabled={isRunning}
                    />
                  }
                  label="Historical Fetch"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <Box display="flex" gap={1}>
                  <StyledButton
                    variant="contained"
                    color="primary"
                    onClick={startSalesSync}
                    disabled={isRunning}
                    startIcon={<PlayArrow />}
                    fullWidth
                  >
                    Start Sync
                  </StyledButton>
                  <StyledButton
                    variant="outlined"
                    color="secondary"
                    onClick={stopSalesSync}
                    disabled={!isRunning}
                    startIcon={<Stop />}
                  >
                    Stop
                  </StyledButton>
                </Box>
              </Grid>
            </Grid>
          </StyledPaper>

          {/* Historical sync info */}
          {historical && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>Historical Sync Mode</AlertTitle>
              This will process sales data month by month starting from 2020. 
              This approach is much faster and provides better progress tracking than processing all data at once.
            </Alert>
          )}

          {/* Status */}
          {status.message && (
            <Alert 
              severity={status.type === 'success' ? 'success' : status.type === 'error' ? 'error' : 'info'}
              icon={getStatusIcon()}
              sx={{ mt: 2 }}
            >
              {status.message}
            </Alert>
          )}

          {/* Overall Progress */}
          {isRunning && (
            <StyledCard>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6">
                    Sync Progress
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    {totalMonths > 1 && (
                      <Chip 
                        label={`Month ${currentMonthIndex}/${totalMonths}`}
                        color="primary" 
                        size="small"
                        icon={<CalendarToday />}
                      />
                    )}
                    {connectionAttempts > 0 && (
                      <Chip 
                        label={`Reconnecting (${connectionAttempts}/${maxReconnectAttempts})`}
                        color="warning" 
                        size="small"
                        icon={<Sync />}
                      />
                    )}
                  </Box>
                </Box>
                {currentMonth && (
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Currently processing: {currentMonth}
                  </Typography>
                )}
                <LinearProgress 
                  variant="determinate" 
                  value={progress} 
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="textSecondary">
                    {progressText || 'Initializing...'}
                  </Typography>
                  <Chip 
                    label={`${progress.toFixed(1)}%`} 
                    color="primary" 
                    size="small"
                  />
                </Box>
              </CardContent>
            </StyledCard>
          )}

          {/* Monthly Progress */}
          {monthlyProgress.length > 0 && (
            <StyledCard>
              <CardHeader 
                title="Monthly Processing Progress"
                avatar={<CalendarToday color="primary" />}
              />
              <CardContent>
                <MonthlyStatsGrid>
                  {monthlyProgress.map((monthProgress, index) => (
                    <MonthlyProgressCard 
                      key={`${monthProgress.month}-${monthProgress.posName}`}
                      className={monthProgress.status}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="h6">
                            {monthProgress.month}
                          </Typography>
                          <Chip 
                            label={monthProgress.posName}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        
                        {monthProgress.status === 'processing' && (
                          <Box>
                            <Typography variant="body2" color="textSecondary" mb={1}>
                              Fetched: {monthProgress.fetchProgress || 0} sales
                            </Typography>
                            {monthProgress.processTotal > 0 && (
                              <>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={(monthProgress.processProgress / monthProgress.processTotal) * 100}
                                  sx={{ mb: 1, height: 6, borderRadius: 3 }}
                                />
                                <Typography variant="body2" color="textSecondary">
                                  Processing: {monthProgress.processProgress}/{monthProgress.processTotal}
                                </Typography>
                              </>
                            )}
                          </Box>
                        )}
                        
                        {monthProgress.status === 'completed' && monthProgress.monthStats && (
                          <Box>
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="textSecondary">Sales</Typography>
                                <Typography variant="h6" color="success.main">
                                  {monthProgress.monthStats.salesProcessed}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="textSecondary">Items</Typography>
                                <Typography variant="h6" color="primary.main">
                                  {monthProgress.monthStats.itemsProcessed}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="textSecondary">New</Typography>
                                <Typography variant="body1" color="success.main">
                                  {monthProgress.monthStats.newSales}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="textSecondary">Updated</Typography>
                                <Typography variant="body1" color="info.main">
                                  {monthProgress.monthStats.updatedSales}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                        
                        {monthProgress.status === 'error' && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Error color="error" fontSize="small" />
                            <Typography variant="body2" color="error">
                              Processing failed
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </MonthlyProgressCard>
                  ))}
                </MonthlyStatsGrid>
              </CardContent>
            </StyledCard>
          )}

          {/* Monthly Statistics Summary */}
          {monthlyStats.length > 0 && (
            <StyledCard>
              <CardHeader 
                title="Monthly Statistics Summary"
                avatar={<BarChart color="primary" />}
              />
              <CardContent>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell align="right">Total Sales</TableCell>
                        <TableCell align="right">New Sales</TableCell>
                        <TableCell align="right">Updated Sales</TableCell>
                        <TableCell align="right">Items Processed</TableCell>
                        <TableCell align="right">Unmatched Items</TableCell>
                        <TableCell align="right">Match Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monthlyStats
                        .sort((a, b) => new Date(a.year, a.monthNumber - 1) - new Date(b.year, b.monthNumber - 1))
                        .map((monthStat, index) => {
                          const matchRate = monthStat.totalItems > 0 
                            ? ((monthStat.totalItems / (monthStat.totalItems + monthStat.totalUnmatchedItems)) * 100).toFixed(1)
                            : '0.0';
                            
                          return (
                            <TableRow key={index} hover>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <CalendarToday fontSize="small" color="primary" />
                                  <Typography variant="body2" fontWeight={600}>
                                    {monthStat.month}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={600} color="primary">
                                  {monthStat.totalSales.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="success.main">
                                  {monthStat.totalNewSales.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="info.main">
                                  {monthStat.totalUpdatedSales.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {monthStat.totalItems.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="warning.main">
                                  {monthStat.totalUnmatchedItems.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip 
                                  label={`${matchRate}%`}
                                  size="small"
                                  color={parseFloat(matchRate) >= 80 ? 'success' : parseFloat(matchRate) >= 60 ? 'warning' : 'error'}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </StyledCard>
          )}

          {/* Statistics */}
          {stats && (
            <StyledCard>
              <CardHeader 
                title="Sync Statistics"
                avatar={<Assessment color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={2}>
                    <StatCard>
                      <CardContent>
                        <Typography variant="h4">{stats.totalSalesProcessed}</Typography>
                        <Typography variant="body2">Sales Processed</Typography>
                      </CardContent>
                    </StatCard>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <StatCard>
                      <CardContent>
                        <Typography variant="h4">{stats.newSales}</Typography>
                        <Typography variant="body2">New Sales</Typography>
                      </CardContent>
                    </StatCard>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <StatCard>
                      <CardContent>
                        <Typography variant="h4">{stats.updatedSales}</Typography>
                        <Typography variant="body2">Updated Sales</Typography>
                      </CardContent>
                    </StatCard>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <StatCard>
                      <CardContent>
                        <Typography variant="h4">{stats.processedItems}</Typography>
                        <Typography variant="body2">Items Processed</Typography>
                      </CardContent>
                    </StatCard>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <StatCard>
                      <CardContent>
                        <Typography variant="h4">{stats.totalUnmatchedItems}</Typography>
                        <Typography variant="body2">Unmatched Items</Typography>
                      </CardContent>
                    </StatCard>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <StatCard>
                      <CardContent>
                        <Typography variant="h4">{formatDuration(stats.duration)}</Typography>
                        <Typography variant="body2">Duration</Typography>
                      </CardContent>
                    </StatCard>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          )}

          {/* Comprehensive Summary Cards */}
          {detailedSummary && (
            <StyledCard>
              <CardHeader 
                title="Sync Summary & Financial Analytics"
                avatar={<Assessment color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SummaryCard>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          Sales Overview
                        </Typography>
                        <Box className="metric-row">
                          <Typography className="metric-label">Total Sales Processed:</Typography>
                          <Typography className="metric-value">{detailedSummary.sync_overview.total_sales_processed.toLocaleString()}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">New Sales Added:</Typography>
                          <Typography className="metric-value new">{detailedSummary.sync_overview.new_sales_added.toLocaleString()}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Existing Sales Updated:</Typography>
                          <Typography className="metric-value updated">{detailedSummary.sync_overview.existing_sales_updated.toLocaleString()}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Processing Time:</Typography>
                          <Typography className="metric-value">{detailedSummary.timing.total_duration}</Typography>
                        </Box>
                      </CardContent>
                    </SummaryCard>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SummaryCard>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          Items Analytics
                        </Typography>
                        <Box className="metric-row">
                          <Typography className="metric-label">Items Processed:</Typography>
                          <Typography className="metric-value">{detailedSummary.sync_overview.total_items_processed.toLocaleString()}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Unmatched Items:</Typography>
                          <Typography className="metric-value warning">{detailedSummary.sync_overview.unmatched_items_count.toLocaleString()}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Skipped Non-Inventory:</Typography>
                          <Typography className="metric-value skipped">{detailedSummary.sync_overview.skipped_non_inventory_items.toLocaleString()}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Match Rate:</Typography>
                          <Typography className="metric-value rate">{detailedSummary.processing_details.items_match_rate}</Typography>
                        </Box>
                      </CardContent>
                    </SummaryCard>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SummaryCard>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          System Performance
                        </Typography>
                        <Box className="metric-row">
                          <Typography className="metric-label">POS Systems Processed:</Typography>
                          <Typography className="metric-value">{detailedSummary.sync_overview.pos_systems_processed}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Success Rate:</Typography>
                          <Typography className="metric-value success">{detailedSummary.processing_details.success_rate}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Errors Encountered:</Typography>
                          <Typography className="metric-value error">{detailedSummary.sync_overview.errors_encountered}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Non-Inventory Skip Rate:</Typography>
                          <Typography className="metric-value">{detailedSummary.processing_details.non_inventory_skip_rate}</Typography>
                        </Box>
                      </CardContent>
                    </SummaryCard>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SummaryCard>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          Timing & Date Range
                        </Typography>
                        <Box className="metric-row">
                          <Typography className="metric-label">Date Range:</Typography>
                          <Typography className="metric-value date">
                            {detailedSummary.sync_overview.date_range.start} to {detailedSummary.sync_overview.date_range.end}
                          </Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Started At:</Typography>
                          <Typography className="metric-value">{new Date(detailedSummary.timing.started_at).toLocaleString()}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Completed At:</Typography>
                          <Typography className="metric-value">{new Date(detailedSummary.timing.completed_at).toLocaleString()}</Typography>
                        </Box>
                        <Box className="metric-row">
                          <Typography className="metric-label">Historical Fetch:</Typography>
                          <Typography className="metric-value">{detailedSummary.sync_overview.was_historical_fetch ? 'Yes' : 'No'}</Typography>
                        </Box>
                      </CardContent>
                    </SummaryCard>
                  </Grid>
                  <Grid item xs={12}>
                    <SummaryCard>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary">
                          Processing Breakdown
                        </Typography>
                        <Box display="flex" justifyContent="space-around" alignItems="center">
                          <Box textAlign="center">
                            <Typography variant="body2" color="textSecondary">New vs Updated Sales</Typography>
                            <Box className="ratio-values">
                              <Typography className="new-ratio">
                                New: {detailedSummary.processing_details.new_vs_updated_ratio.new_percentage}
                              </Typography>
                              <Typography className="updated-ratio">
                                Updated: {detailedSummary.processing_details.new_vs_updated_ratio.updated_percentage}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </SummaryCard>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          )}

          {/* Logs */}
          <StyledCard>
            <CardHeader 
              title="Sync Log"
              avatar={<Timeline color="primary" />}
              action={
                <Tooltip title="Clear logs">
                  <IconButton onClick={clearLogs} size="small">
                    <Clear />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <LogContainer ref={logContainerRef}>
                {logs.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: '20px' }}>
                    No log entries yet. Start a sync to see progress logs.
                  </Typography>
                ) : (
                  logs.map((log) => (
                    <div key={log.id} className={`log-entry ${log.type}`}>
                      [{log.timestamp}] {log.message}
                    </div>
                  ))
                )}
              </LogContainer>
            </CardContent>
          </StyledCard>

          {/* Sales Data Table */}
          {salesData.length > 0 && (
            <StyledCard>
              <CardHeader 
                title={`Recent Sales (${salesData.length.toLocaleString()} records)`}
                avatar={<Assessment color="primary" />}
                action={
                  salesData.length > 10 && (
                    <StyledButton
                      size="small"
                      onClick={() => setShowFullSalesTable(!showFullSalesTable)}
                    >
                      {showFullSalesTable ? 'Show Less' : `Show All ${salesData.length.toLocaleString()}`}
                    </StyledButton>
                  )
                }
              />
              <CardContent>
                {/* Sales Summary */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Total Revenue</Typography>
                      <Typography variant="h6" color="primary">
                        ${salesData.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Total Items</Typography>
                      <Typography variant="h6" color="primary">
                        {salesData.reduce((sum, sale) => sum + parseInt(sale.item_count || 0), 0).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Total Quantity</Typography>
                      <Typography variant="h6" color="primary">
                        {salesData.reduce((sum, sale) => sum + parseInt(sale.total_quantity || 0), 0).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary">Avg Sale Amount</Typography>
                      <Typography variant="h6" color="primary">
                        ${(salesData.reduce((sum, sale) => sum + parseFloat(sale.total_amount || 0), 0) / salesData.length).toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <TableContainer sx={{ maxHeight: showFullSalesTable ? 600 : 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Sale ID</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>POS System</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Items</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Line Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(showFullSalesTable ? salesData : salesData.slice(0, 10)).map((sale, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {sale.sale_id}
                            </Typography>
                          </TableCell>
                          <TableCell>{new Date(sale.sale_date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary">
                              {sale.location_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={sale.pos_name} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              ${parseFloat(sale.total_amount || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={sale.item_count || 0} size="small" color="primary" />
                          </TableCell>
                          <TableCell align="right">{sale.total_quantity || 0}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              ${parseFloat(sale.total_line_amount || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {!showFullSalesTable && salesData.length > 10 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing 10 of {salesData.length.toLocaleString()} sales records
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </StyledCard>
          )}

          {/* Unmatched Items Table */}
          {unmatchedItems.length > 0 && (
            <StyledCard>
              <CardHeader 
                title={`Unmatched Items Analysis (${unmatchedItems.length.toLocaleString()} unique items)`}
                avatar={<Warning color="warning" />}
                action={
                  unmatchedItems.length > 20 && (
                    <StyledButton
                      size="small"
                      onClick={() => setShowFullUnmatchedTable(!showFullUnmatchedTable)}
                    >
                      {showFullUnmatchedTable ? 'Show Less' : `Show All ${unmatchedItems.length.toLocaleString()}`}
                    </StyledButton>
                  )
                }
              />
              <CardContent>
                {/* Unmatched Items Summary */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', color: 'warning.contrastText', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">Total Unmatched</Typography>
                      <Typography variant="h6">
                        {unmatchedItems.length.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">Total Occurrences</Typography>
                      <Typography variant="h6">
                        {unmatchedItems.reduce((sum, item) => sum + parseInt(item.occurrence_count || 0), 0).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">Most Common Item</Typography>
                      <Typography variant="h6" sx={{ fontSize: '0.9rem' }}>
                        {unmatchedItems[0]?.item_name?.substring(0, 20) + (unmatchedItems[0]?.item_name?.length > 20 ? '...' : '')}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2">Max Occurrences</Typography>
                      <Typography variant="h6">
                        {unmatchedItems[0]?.occurrence_count || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <TableContainer sx={{ maxHeight: showFullUnmatchedTable ? 600 : 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item Name</TableCell>
                        <TableCell align="right">Occurrences</TableCell>
                        <TableCell>Locations</TableCell>
                        <TableCell>POS Systems</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(showFullUnmatchedTable ? unmatchedItems : unmatchedItems.slice(0, 20)).map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontFamily: 'monospace', 
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {item.item_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={item.occurrence_count} 
                              color={item.occurrence_count > 10 ? 'error' : item.occurrence_count > 5 ? 'warning' : 'default'} 
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary" sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {item.locations?.join(', ')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {item.pos_systems?.map((pos, idx) => (
                              <Chip 
                                key={idx} 
                                label={pos} 
                                size="small" 
                                variant="outlined" 
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {!showFullUnmatchedTable && unmatchedItems.length > 20 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing 20 of {unmatchedItems.length.toLocaleString()} unmatched items
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </StyledCard>
          )}
        </PageContainer>
      }
    />
  );
};

export default SalesSync; 